import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Brain,
  Bot,
  GitGraph,
  Database,
  Code,
  ChevronRight,
  CheckCircle,
  Cpu,
  Network,
  BarChart,
  MessageSquare,
  FileText,
  Lightbulb,
  ArrowRight
} from "lucide-react";

const SolutionsPage = () => {
  // Data for our solutions categories
  const solutionCategories = [
    {
      id: "gen-ai",
      title: "Generative AI",
      description: "Harness the power of cutting-edge generative AI technologies for content creation, analysis, and decision support.",
      icon: <Brain className="w-6 h-6 text-blue-600" />,
      link: "/solution/gen-ai-solutions",
      features: [
        "Custom large language model development",
        "Fine-tuned domain-specific models",
        "Content generation & summarization",
        "Advanced RAG implementations"
      ],
      bgGradient: "from-blue-50 to-white",
      btnColor: "bg-blue-600 hover:bg-blue-700",
      iconBg: "bg-gradient-to-r from-blue-600 to-purple-600"
    },
    {
      id: "ai-agents",
      title: "AI Agents",
      description: "Build autonomous AI agents that can plan, reason, and execute complex tasks for your business processes.",
      icon: <Bot className="w-6 h-6 text-purple-600" />,
      link: "/solution/ai-agents-solutions", 
      features: [
        "Autonomous workflow automation",
        "Task planning & execution",
        "Multi-agent systems",
        "Customer service automation"
      ],
      bgGradient: "from-purple-50 to-white",
      btnColor: "bg-purple-600 hover:bg-purple-700",
      iconBg: "bg-gradient-to-r from-purple-600 to-pink-600"
    },
    {
      id: "custom-ai",
      title: "Custom AI Solutions",
      description: "Tailor-made AI solutions to address your unique business challenges with industry-specific models.",
      icon: <Code className="w-6 h-6 text-indigo-600" />,
      link: "/solution/custom-ai-solutions",
      features: [
        "Industry-specific AI development",
        "MCP (Model Context Protocol) integration",
        "Enterprise data security",
        "Scalable infrastructure"
      ],
      bgGradient: "from-indigo-50 to-white",
      btnColor: "bg-indigo-600 hover:bg-indigo-700",
      iconBg: "bg-gradient-to-r from-indigo-600 to-blue-600"
    }
  ];

  // Data for enterprise benefits section
  const enterpriseBenefits = [
    {
      title: "Accelerate Innovation",
      description: "Fast-track your innovation roadmap with AI-powered solutions that can be deployed in weeks, not months.",
      icon: <Lightbulb className="w-10 h-10 text-yellow-500" />
    },
    {
      title: "Enhance Decision Making",
      description: "Leverage data-driven insights and AI recommendations to make better business decisions.",
      icon: <BarChart className="w-10 h-10 text-blue-500" />
    },
    {
      title: "Automate Workflows",
      description: "Eliminate repetitive tasks and streamline processes with intelligent automation.",
      icon: <Network className="w-10 h-10 text-green-500" />
    },
    {
      title: "Scale Operations",
      description: "Handle growing demands without proportional increases in resources or personnel.",
      icon: <Cpu className="w-10 h-10 text-purple-500" />
    }
  ];

  return (
    <div className="bg-gray-50">
      <Helmet>
        <title>Enterprise AI Solutions | AI Guru</title>
        <meta 
          name="description" 
          content="Transform your business with AI Guru's enterprise solutions: Generative AI, Agentic AI, and custom AI solutions tailored to your industry needs." 
        />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-200 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
                Enterprise-Grade 
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 mt-2">
                  AI Solutions
                </span>
              </h1>
              <p className="mt-6 text-xl text-gray-600 max-w-3xl">
                Transform your business with cutting-edge AI technologies. Our solutions leverage the latest advancements in 
                <span className="font-semibold text-blue-700"> Generative AI</span>,
                <span className="font-semibold text-purple-700"> Agentic AI</span>, and
                <span className="font-semibold text-indigo-700"> Model Context Protocol (MCP)</span> to 
                drive innovation and efficiency.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link 
                  to="/contactUs" 
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link 
                  to="/courses" 
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Explore Training Programs
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-blue-600 rounded-3xl opacity-10 blur-3xl transform -rotate-6"></div>
              <div className="relative">
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/9e534fec-3f79-4d3d-b6ef-955ddeedd6b9/genAI.png?format=1000w" 
                  alt="AI Solutions Visualization" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Categories Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Our AI Solution Categories
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI solutions tailored to your enterprise needs, from generative AI to autonomous agents and custom implementations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutionCategories.map((category) => (
              <div 
                key={category.id} 
                className={`bg-gradient-to-br ${category.bgGradient} rounded-2xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl`}
              >
                <div className="p-8">
                  <div className={`w-14 h-14 rounded-xl ${category.iconBg} flex items-center justify-center mb-6`}>
                    {category.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {category.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {category.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-700">
                        <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to={category.link} 
                    className={`inline-flex items-center px-5 py-3 ${category.btnColor} text-white font-medium rounded-md shadow-md hover:shadow-lg transition-all duration-200`}
                  >
                    Learn More
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technology Stack Section */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Our Enterprise AI Stack
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              We leverage cutting-edge technologies to build robust, scalable AI solutions for enterprise needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Large Language Models */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Brain className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Large Language Models</h3>
              <p className="text-gray-600">
                Custom-tuned foundation models optimized for your specific industry and use cases.
              </p>
            </div>

            {/* Model Context Protocol (MCP) */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <GitGraph className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Model Context Protocol</h3>
              <p className="text-gray-600">
                Advanced context management for enhanced AI security, performance, and enterprise integration.
              </p>
            </div>

            {/* Vector Databases */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <Database className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Vector Databases</h3>
              <p className="text-gray-600">
                High-performance retrieval systems for semantic search and knowledge retrieval.
              </p>
            </div>

            {/* Enterprise Integration */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <Network className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise Integration</h3>
              <p className="text-gray-600">
                Seamless integration with your existing systems, APIs, and data infrastructure.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise Benefits Section */}
      <div className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold sm:text-4xl">
              Why Choose AI Guru for Enterprise Solutions
            </h2>
            <p className="mt-4 text-xl text-gray-300 max-w-3xl mx-auto">
              Our solutions deliver tangible business outcomes with rapid time-to-value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {enterpriseBenefits.map((benefit, index) => (
              <div key={index} className="bg-gray-800 p-8 rounded-xl">
                <div className="mx-auto flex items-center justify-center w-16 h-16 bg-gray-700 rounded-lg mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-center mb-4">{benefit.title}</h3>
                <p className="text-gray-300 text-center">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industry Solutions Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Industry-Specific Solutions
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              We've deployed AI solutions across diverse industries, each with unique challenges and requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Financial Services */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Financial Services</h3>
              <p className="text-gray-600 mb-4">
                AI-driven risk assessment, fraud detection, and personalized financial advisory services.
              </p>
              <div className="border-t border-blue-100 pt-4 mt-4">
                <span className="text-sm text-blue-600 font-medium">Technologies:</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Agentic AI
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Custom LLMs
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    MCP Protocol
                  </span>
                </div>
              </div>
            </div>

            {/* Healthcare */}
            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Healthcare</h3>
              <p className="text-gray-600 mb-4">
                Clinical decision support, patient data analysis, and administrative workflow optimization.
              </p>
              <div className="border-t border-green-100 pt-4 mt-4">
                <span className="text-sm text-green-600 font-medium">Technologies:</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    RAG Architecture
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Vector DBs
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    MCP Protocol
                  </span>
                </div>
              </div>
            </div>

            {/* Manufacturing */}
            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-xl border border-orange-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Manufacturing</h3>
              <p className="text-gray-600 mb-4">
                Predictive maintenance, supply chain optimization, and quality control systems.
              </p>
              <div className="border-t border-orange-100 pt-4 mt-4">
                <span className="text-sm text-orange-600 font-medium">Technologies:</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Agentic AI
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    IoT Integration
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Custom Models
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Our AI Implementation Process
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology for successful enterprise AI adoption.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Step 1: Assessment */}
            <div className="relative">
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">1</div>
              <div className="bg-white p-8 rounded-xl shadow-md h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Assessment</h3>
                <p className="text-gray-600">
                  We analyze your current systems, identify opportunities, and define clear objectives.
                </p>
              </div>
            </div>

            {/* Step 2: Strategy */}
            <div className="relative">
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">2</div>
              <div className="bg-white p-8 rounded-xl shadow-md h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Strategy</h3>
                <p className="text-gray-600">
                  We develop a comprehensive roadmap with technology selection and implementation plan.
                </p>
              </div>
            </div>

            {/* Step 3: Development */}
            <div className="relative">
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">3</div>
              <div className="bg-white p-8 rounded-xl shadow-md h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Development</h3>
                <p className="text-gray-600">
                  We build, test, and refine your AI solution with continuous feedback loops.
                </p>
              </div>
            </div>

            {/* Step 4: Deployment & Support */}
            <div className="relative">
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">4</div>
              <div className="bg-white p-8 rounded-xl shadow-md h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Deployment & Support</h3>
                <p className="text-gray-600">
                  We ensure smooth integration, provide training, and offer ongoing optimization.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expert Team Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-6">
                World-Class AI Expertise
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Our team combines deep technical expertise with industry knowledge to deliver transformative AI solutions. Led by Silicon Valley veteran and global AI thought leader Ritesh Vajariya, we bring unparalleled experience in enterprise AI implementation.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Former leadership at AWS, driving SageMaker and Amazon Bedrock strategies</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Contributions to groundbreaking LLM projects like Falcon and BloombergGPT</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">20+ countries reached through workshops and training</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">20,000+ professionals trained in AI technologies</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <img
                src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/dec478a7-180a-4cb2-88e7-3cf12ccc2090/Ritesh-self-1.png?format=1500w"
                alt="Ritesh Vajariya"
                className="rounded-lg shadow-lg w-full object-cover"
              />
              <img
                src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/70e25ddf-3dc2-43a0-a7cc-d77329a10479/mexico-event-1.jpeg?format=1500w"
                alt="AI Training Session"
                className="rounded-lg shadow-lg w-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
            Ready to Transform Your Enterprise with AI?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Schedule a consultation with our experts to discuss how our AI solutions can address your specific business challenges.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contactUs" 
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md shadow-sm text-blue-700 bg-white hover:bg-blue-50"
            >
              Schedule Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link 
              to="/courses" 
              className="inline-flex items-center px-8 py-4 border border-white text-lg font-medium rounded-md text-white hover:bg-white/10"
            >
              Explore Training Programs
            </Link>
          </div>
          <div className="mt-8 text-blue-200 flex flex-col sm:flex-row items-center justify-center gap-8">
            <div className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              <span>Download AI Solutions Whitepaper</span>
            </div>
            <div className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              <span>Request a Demo</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionsPage;
